import React, { useState, useEffect, useCallback, useRef } from 'react';
import Select from 'react-select';
import {
  Search, MapPin, Building2, FlaskConical,
  DollarSign, GraduationCap, ChevronDown,
  Filter, X, Calendar, Globe
} from 'lucide-react';
import institutionOptions from '../../data/institution_options.json';
import doctoralFields from '../../data/doctoral_fields.json';
import './StudentDashboard.css';

const fieldOptions = doctoralFields.map(({ code, field }) => ({
  value: field,
  label: `${field} (${code})`
}));

const fundingStatusOptions = [
  { value: 'Fully Funded', label: 'Fully Funded' },
  { value: 'Partially Funded', label: 'Partially Funded' },
  { value: 'Not Funded', label: 'Not Funded' }
];

const fundingSourceOptions = [
  { value: 'Grant', label: 'Grant' },
  { value: 'Teaching Assistant', label: 'Teaching Assistant' },
  { value: 'Research Assistant', label: 'Research Assistant' },
  { value: 'Fellowship', label: 'Fellowship' },
  { value: 'Other', label: 'Other' }
];

const labSizeOptions = [
  { value: 'Small (1-10 people)', label: 'Small (1-10)' },
  { value: 'Medium (10-15 people)', label: 'Medium (10-15)' },
  { value: 'Large (15+ people)', label: 'Large (15+)' }
];

const pubRateOptions = [
  { value: 'Low (0-2 papers/year)', label: 'Low (0-2/yr)' },
  { value: 'Medium (2-5 papers/year)', label: 'Medium (2-5/yr)' },
  { value: 'High (5+ papers/year)', label: 'High (5+/yr)' }
];

const isSectionActive = (id, filters) => {
  switch (id) {
    case 'institutions': return filters?.institutions?.length > 0;
    case 'fields': return filters?.fields?.length > 0;
    case 'location': return !!(filters?.city || filters?.country);
    case 'funding': return !!(filters?.fully_funded || filters?.funding_source);
    case 'environment': return !!(filters?.lab_size || filters?.publication_rate);
    case 'requirements': return !!filters?.degree_level;
    case 'date': return !!(filters?.start_semester || filters?.start_year);
    default: return false;
  }
};

const FilterSection = ({ id, label, icon: Icon, activeSection, toggleSection, filters }) => (
  <button
    type="button"
    className={`std-filter-tag-btn ${activeSection === id ? 'active' : ''} ${isSectionActive(id, filters) ? 'has-value' : ''}`}
    onClick={() => toggleSection(id)}
  >
    <Icon size={16} />
    <span>{label}</span>
    <ChevronDown size={14} className={`chevron ${activeSection === id ? 'rotated' : ''}`} />
  </button>
);

function StudentFilterBar({ onFilterChange, initialFilters }) {
  const [filters, setFilters] = useState(initialFilters || {
    title: '',
    description: '',
    institutions: [],
    fields: [],
    start_semester: '',
    start_year: '',
    city: '',
    country: '',
    native: false,
    fully_funded: '',
    funding_source: '',
    lab_size: '',
    publication_rate: '',
    degree_level: '',
  });

  const [activeSection, setActiveSection] = useState(null);
  const barRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (barRef.current && !barRef.current.contains(event.target)) {
        setActiveSection(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync local filters with parent if parent updates (e.g. badge removed)
  useEffect(() => {
    if (initialFilters) {
      const parentStr = JSON.stringify(initialFilters);
      // We use a functional update to check against current state without adding it to dependencies
      setFilters(prev => {
        const currentStr = JSON.stringify(prev);
        if (parentStr !== currentStr) {
          return initialFilters;
        }
        return prev;
      });
    }
  }, [initialFilters]); // Only re-run when parent filters genuinely change

  // Handle parent notification with a debounce to prevent focus loss and lag
  useEffect(() => {
    const handler = setTimeout(() => {
      if (onFilterChange) {
        onFilterChange(filters);
      }
    }, 400);
    return () => clearTimeout(handler);
  }, [filters, onFilterChange]);

  const toggleSection = useCallback((section) => {
    setActiveSection(prev => prev === section ? null : section);
  }, []);

  const handleTextInputChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleSelectChange = (field, selectedOption) => {
    setFilters(prev => ({ ...prev, [field]: selectedOption ? selectedOption.value : '' }));
  };

  const handleMultiSelectChange = (field, selectedOptions) => {
    const selectedValues = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
    setFilters(prev => ({ ...prev, [field]: selectedValues }));
  };

  const clearAllFilters = () => {
    const defaultFilters = {
      title: '',
      description: '',
      institutions: [],
      fields: [],
      start_semester: '',
      start_year: '',
      city: '',
      country: '',
      native: false,
      fully_funded: '',
      funding_source: '',
      lab_size: '',
      publication_rate: '',
      degree_level: '',
    };
    setFilters(defaultFilters);
    setActiveSection(null);
  };

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      minHeight: '36px',
      fontSize: '13px',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
      boxShadow: 'none',
      '&:hover': { border: '1px solid #3b82f6' }
    }),
    option: (base, state) => ({
      ...base,
      fontSize: '13px',
      backgroundColor: state.isSelected ? '#061941' : state.isFocused ? '#f3f4f6' : 'white',
      color: state.isSelected ? 'white' : '#374151',
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: '#e0f2fe',
      borderRadius: '4px',
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: '#0369a1',
      fontSize: '11px',
    })
  };

  return (
    <div className="std-top-filter-bar" ref={barRef}>
      <div className="std-search-wrapper">
        <Search size={18} className="std-search-icon" />
        <input
          type="text"
          value={filters.title}
          onChange={(e) => handleTextInputChange('title', e.target.value)}
          placeholder="Search positions by title..."
          className="std-main-search"
        />
      </div>

      <div className="std-filter-tags-scroll">
        <button
          className={`std-native-toggle ${filters.native ? 'active' : ''}`}
          onClick={() => setFilters(prev => ({ ...prev, native: !prev.native }))}
        >
          NeuHawk Native
        </button>

        <FilterSection
          id="institutions"
          label="Institutions"
          icon={Building2}
          activeSection={activeSection}
          toggleSection={toggleSection}
          filters={filters}
        />

        <FilterSection
          id="fields"
          label="Research Fields"
          icon={FlaskConical}
          activeSection={activeSection}
          toggleSection={toggleSection}
          filters={filters}
        />

        <FilterSection
          id="location"
          label="Location"
          icon={MapPin}
          activeSection={activeSection}
          toggleSection={toggleSection}
          filters={filters}
        />

        <FilterSection
          id="funding"
          label="Funding"
          icon={DollarSign}
          activeSection={activeSection}
          toggleSection={toggleSection}
          filters={filters}
        />

        <FilterSection
          id="environment"
          label="Lab"
          icon={Globe}
          activeSection={activeSection}
          toggleSection={toggleSection}
          filters={filters}
        />

        <FilterSection
          id="requirements"
          label="Requirements"
          icon={GraduationCap}
          activeSection={activeSection}
          toggleSection={toggleSection}
          filters={filters}
        />

        <FilterSection
          id="date"
          label="Start Date"
          icon={Calendar}
          activeSection={activeSection}
          toggleSection={toggleSection}
          filters={filters}
        />


        <button className="std-clear-btn" onClick={clearAllFilters}>
          <X size={14} />
          Clear
        </button>
      </div>

      {activeSection && (
        <div className="std-filter-expansion-panel" onClick={(e) => e.stopPropagation()}>
          <div className="std-expansion-content">
            {activeSection === 'institutions' && (
              <div className="std-dropdown-inner">
                <label className="std-panel-label">Target Institutions</label>
                <Select
                  isMulti
                  options={institutionOptions}
                  value={institutionOptions.filter(opt => filters.institutions.includes(opt.value))}
                  onChange={(opts) => handleMultiSelectChange('institutions', opts)}
                  styles={customSelectStyles}
                  placeholder="Select institutions..."
                />
              </div>
            )}

            {activeSection === 'fields' && (
              <div className="std-dropdown-inner">
                <label className="std-panel-label">Research Fields</label>
                <Select
                  isMulti
                  options={fieldOptions}
                  value={fieldOptions.filter(opt => filters.fields.includes(opt.value))}
                  onChange={(opts) => handleMultiSelectChange('fields', opts)}
                  styles={customSelectStyles}
                  placeholder="Select fields..."
                />
              </div>
            )}

            {activeSection === 'location' && (
              <div className="std-dropdown-inner grid-3">
                <div className="std-input-field">
                  <label>City</label>
                  <input
                    type="text"
                    value={filters.city}
                    onChange={(e) => handleTextInputChange('city', e.target.value)}
                    placeholder="e.g. Atlanta"
                    onFocus={(e) => e.target.select()}
                  />
                </div>
                <div className="std-input-field">
                  <label>Country</label>
                  <input
                    type="text"
                    value={filters.country}
                    onChange={(e) => handleTextInputChange('country', e.target.value)}
                    placeholder="e.g. USA"
                    onFocus={(e) => e.target.select()}
                  />
                </div>
              </div>
            )}

            {activeSection === 'funding' && (
              <div className="std-dropdown-inner grid-3">
                <div className="std-input-field">
                  <label>Status</label>
                  <Select
                    options={fundingStatusOptions}
                    value={fundingStatusOptions.find(opt => opt.value === filters.fully_funded)}
                    onChange={(opt) => handleSelectChange('fully_funded', opt)}
                    styles={customSelectStyles}
                    isClearable
                  />
                </div>
                <div className="std-input-field">
                  <label>Source</label>
                  <Select
                    options={fundingSourceOptions}
                    value={fundingSourceOptions.find(opt => opt.value === filters.funding_source)}
                    onChange={(opt) => handleSelectChange('funding_source', opt)}
                    styles={customSelectStyles}
                    isClearable
                  />
                </div>
              </div>
            )}

            {activeSection === 'environment' && (
              <div className="std-dropdown-inner grid-3">
                <div className="std-input-field">
                  <label>Lab Size</label>
                  <Select
                    options={labSizeOptions}
                    value={labSizeOptions.find(opt => opt.value === filters.lab_size)}
                    onChange={(opt) => handleSelectChange('lab_size', opt)}
                    styles={customSelectStyles}
                    isClearable
                  />
                </div>
                <div className="std-input-field">
                  <label>Publication Rate</label>
                  <Select
                    options={pubRateOptions}
                    value={pubRateOptions.find(opt => opt.value === filters.publication_rate)}
                    onChange={(opt) => handleSelectChange('publication_rate', opt)}
                    styles={customSelectStyles}
                    isClearable
                  />
                </div>
              </div>
            )}

            {activeSection === 'requirements' && (
              <div className="std-dropdown-inner">
                <div className="std-input-field">
                  <label>Minimum Degree Level</label>
                  <input
                    type="text"
                    value={filters.degree_level}
                    onChange={(e) => handleTextInputChange('degree_level', e.target.value)}
                    placeholder="e.g. PhD, Masters"
                    onFocus={(e) => e.target.select()}
                  />
                </div>
              </div>
            )}

            {activeSection === 'date' && (
              <div className="std-dropdown-inner grid-3">
                <div className="std-input-field">
                  <label>Start Semester</label>
                  <select
                    value={filters.start_semester}
                    onChange={(e) => handleTextInputChange('start_semester', e.target.value)}
                  >
                    <option value="">Any</option>
                    <option value="Fall">Fall</option>
                    <option value="Spring">Spring</option>
                    <option value="Summer">Summer</option>
                  </select>
                </div>
                <div className="std-input-field">
                  <label>Start Year</label>
                  <select
                    value={filters.start_year}
                    onChange={(e) => handleTextInputChange('start_year', e.target.value)}
                  >
                    <option value="">Any</option>
                    {Array.from({ length: 6 }, (_, i) => {
                      const year = new Date().getFullYear() + i;
                      return <option key={year} value={year}>{year}</option>;
                    })}
                  </select>
                </div>
              </div>
            )}
          </div>
          <button className="std-panel-close" onClick={() => setActiveSection(null)} title="Close Panel">
            <X size={18} />
          </button>
        </div>
      )}
    </div>
  );
}

export default StudentFilterBar;
